import React, { forwardRef } from 'react';

import type { LucideIcon } from 'lucide-react';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
	label?: string;
	error?: string;
	helperText?: string;
	leftIcon?: LucideIcon;
	rightIcon?: LucideIcon;
	rightButton?: string;
	onRightIconClick?: () => void;
	onRightButtonClick?: () => void;
	rightButtonDisabled?: boolean;
	variant?: 'default' | 'filled' | 'outlined';
	inputSize?: 'sm' | 'md' | 'lg';
	fullWidth?: boolean;
	inputType?: 'text' | 'checkbox' | 'radio';
}

const Input = forwardRef<HTMLInputElement, InputProps>(
	(
		{
			label,
			error,
			helperText,
			leftIcon: LeftIcon,
			rightIcon: RightIcon,
			rightButton,
			onRightIconClick,
			onRightButtonClick,
			rightButtonDisabled = false,
			variant = 'default',
			inputSize = 'md',
			fullWidth = true,
			inputType = 'text',
			className = '',
			id,
			required,
			type,
			...props
		},
		ref,
	) => {
		const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;
		const actualType = type || (inputType === 'text' ? 'text' : inputType);

		// Handle checkbox and radio inputs
		if (actualType === 'checkbox' || actualType === 'radio') {
			return (
				<div className={fullWidth ? 'w-full' : ''}>
					<div className="flex items-center">
						<input
							ref={ref}
							id={inputId}
							type={actualType}
							className={`
                            w-4 h-4 text-primary-600 bg-neutral-100
                            border-neutral-300 rounded focus:ring-primary-500 focus:ring-2
                            ${error ? 'border-sa-error-300 focus:ring-sa-error-500' : ''}
                            ${props.disabled ? 'opacity-50 cursor-not-allowed' : ''}
                            ${className}
                        `
								.trim()
								.replace(/\s+/g, ' ')}
							{...props}
						/>
						{label && (
							<label
								htmlFor={inputId}
								className="ml-2 text-sm font-medium text-gray-700 cursor-pointer"
							>
								{label}
								{required && <span className="text-red-500 ml-1">*</span>}
							</label>
						)}
					</div>
					{error && <p className="mt-1 text-sm text-red-600">{error}</p>}
					{helperText && !error && (
						<p className="mt-1 text-sm text-gray-500">{helperText}</p>
					)}
				</div>
			);
		}

		// Base styles for text inputs using design tokens
		const baseStyles = `
        block border transition-colors duration-150 ease-in-out
        focus:outline-none focus:ring-1 focus:ring-offset-0
        font-primary
    `
			.trim()
			.replace(/\s+/g, ' ');

		// Variant styles using design tokens
		const variantStyles = {
			default: `
            border-neutral-300
            focus:ring-primary-400 focus:border-primary-400
            bg-white
        `
				.trim()
				.replace(/\s+/g, ' '),
			filled: `
            border-neutral-200
            focus:ring-primary-400 focus:border-primary-400
            bg-neutral-50
        `
				.trim()
				.replace(/\s+/g, ' '),
			outlined: `
            border-neutral-300
            focus:ring-primary-400 focus:border-primary-400
            bg-white
        `
				.trim()
				.replace(/\s+/g, ' '),
		};

		// Size styles using standard Tailwind classes with design token values
		const sizeStyles = {
			sm: `py-2 text-sm rounded-md`,
			md: `py-2 text-sm rounded-lg`,
			lg: `py-3 text-base rounded-lg`,
		};

		// Icon sizes
		const iconSizes = {
			sm: 'w-4 h-4',
			md: 'w-4 h-4',
			lg: 'w-5 h-5',
		};

		// Error styles using design tokens
		const errorStyles = error
			? `border-sa-error-300 focus:ring-sa-error-500 focus:border-sa-error-500 bg-error-50`
			: '';

		const horizontalPaddingClasses = [
			LeftIcon ? 'pl-9' : 'pl-4',
			RightIcon || rightButton ? 'pr-12' : 'pr-4',
		].join(' ');

		return (
			<div className={fullWidth ? 'w-full' : ''}>
				{label && (
					<label
						htmlFor={inputId}
						className="block text-sm font-medium text-neutral-700 mb-2 font-primary"
					>
						{label}
						{required && <span className="text-error-500 ml-1">*</span>}
					</label>
				)}

				<div className="relative">
					{LeftIcon && (
						<div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
							<LeftIcon
								className={`${iconSizes[inputSize]} text-neutral-400`}
							/>
						</div>
					)}

					<input
						ref={ref}
						id={inputId}
						type={actualType}
						className={`
            ${baseStyles}
            ${errorStyles || variantStyles[variant]}
            ${sizeStyles[inputSize]}
            ${horizontalPaddingClasses}
            ${fullWidth ? 'w-full' : ''}
            ${props.disabled ? 'opacity-50 cursor-not-allowed' : ''}
            ${className}
          `
							.trim()
							.replace(/\s+/g, ' ')}
						{...props}
					/>

					{RightIcon && (
						<div className="absolute inset-y-0 right-3 flex items-center">
							{onRightIconClick ? (
								<button
									type="button"
									onClick={onRightIconClick}
									className="text-neutral-400 hover:text-neutral-600 transition-colors"
								>
									<RightIcon className={iconSizes[inputSize]} />
								</button>
							) : (
								<RightIcon
									className={`${iconSizes[inputSize]} text-neutral-400`}
								/>
							)}
						</div>
					)}

					{rightButton && (
						<div className="absolute inset-y-0 right-0 pr-1 flex items-center">
							<button
								type="button"
								onClick={onRightButtonClick}
								disabled={rightButtonDisabled}
								className="px-3 py-1 text-sm font-medium text-purple-600 hover:text-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
							>
								{rightButton}
							</button>
						</div>
					)}
				</div>

				{error && <p className="mt-1 text-sm text-red-600">{error}</p>}

				{helperText && !error && (
					<p className="mt-1 text-sm text-gray-500">{helperText}</p>
				)}
			</div>
		);
	},
);

Input.displayName = 'Input';

export default Input;

// Textarea component
export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
	label?: string;
	error?: string;
	helperText?: string;
	variant?: 'default' | 'filled' | 'outlined';
	fullWidth?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
	(
		{
			label,
			error,
			helperText,
			variant = 'default',
			fullWidth = true,
			className = '',
			id,
			required,
			...props
		},
		ref,
	) => {
		const textareaId =
			id || `textarea-${Math.random().toString(36).substr(2, 9)}`;

		// Base styles
		const baseStyles =
			'block border transition-colors focus:outline-none focus:ring-2 focus:ring-offset-0 resize-none';

		// Variant styles
		const variantStyles = {
			default:
				'border-gray-300 focus:ring-purple-500 focus:border-purple-500 bg-white',
			filled:
				'border-gray-200 focus:ring-purple-500 focus:border-purple-500 bg-gray-50',
			outlined:
				'border-2 border-gray-300 focus:ring-purple-500 focus:border-purple-500 bg-white',
		};

		// Error styles
		const errorStyles = error
			? 'border-red-300 focus:ring-red-500 focus:border-red-500 bg-red-50'
			: '';

		return (
			<div className={fullWidth ? 'w-full' : ''}>
				{label && (
					<label
						htmlFor={textareaId}
						className="block text-sm font-medium text-gray-700 mb-2"
					>
						{label}
						{required && <span className="text-red-500 ml-1">*</span>}
					</label>
				)}

				<textarea
					ref={ref}
					id={textareaId}
					className={`
          ${baseStyles}
          ${errorStyles || variantStyles[variant]}
          px-3 py-2 text-sm rounded-lg
          ${fullWidth ? 'w-full' : ''}
          ${props.disabled ? 'opacity-50 cursor-not-allowed' : ''}
          ${className}
        `
						.trim()
						.replace(/\s+/g, ' ')}
					{...props}
				/>

				{error && <p className="mt-1 text-sm text-red-600">{error}</p>}

				{helperText && !error && (
					<p className="mt-1 text-sm text-gray-500">{helperText}</p>
				)}
			</div>
		);
	},
);

Textarea.displayName = 'Textarea';
